import {Injectable} from '@angular/core';
import {Browser} from '@capacitor/browser';
import {Capacitor} from '@capacitor/core';
import {ModalController} from '@ionic/angular';
import {TranslateService} from '@ngx-translate/core';
import {ToastManagerService} from '../Platform/Ionic/toast-manager.service';
import {VideosViewerPage} from '../../Pages/Shared/videos-viewer/videos-viewer.page';
import {GalleryPage} from '../../Pages/Shared/gallery/gallery.page';

export type fileType = 'image' | 'video' | 'document';

@Injectable({
  providedIn: 'root'
})
export class FileViewerManagerService {

  private readonly extensionMap: Map<string, fileType> = new Map([
    ...['png', 'jpeg', 'jpg', 'webp', 'gif', 'svg', 'avif', 'tiff', 'bmp'].map(ext => [ext, 'image'] as [string, fileType]),
    ...['mp4', 'mov', 'mkv', 'avi', 'webm', 'wmv', 'flv', 'mpeg', 'mpg', '3gp', 'quicktime'].map(ext => [ext, 'video'] as [string, fileType]),
    ...['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'odt', 'ods', 'odp'].map(ext => [ext, 'document'] as [string, fileType]),
  ]);

  private readonly availableIcons: string[] = ['pdf', 'csv', 'jpg', 'png', 'svg', 'txt', 'zip'];
  private readonly googleViewerSupported: string[] = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];

  constructor(
    protected modalCtrl: ModalController,
    protected toastCtrl: ToastManagerService,
    protected translate: TranslateService,
  ) {
  }


  /**
   * Obtener el tipo de archivo basado en su extensión (imágenes || videos || documentos)
   *
   */
  public getFileType(file: string, extension?: string): fileType | null {
    return this.extensionMap.get(this.getExtension(file, extension)) || null;
  }


  /**
   * Obtener el nombre del icono para un archivo
   *
   */
  public getFileIcon(file: string, extension?: string): string {
    const type: fileType | null = this.getFileType(file, extension);

    if (type === 'image') {
      return 'image';
    }

    if (type === 'video') {
      return 'file-video';
    }

    const ext: string = this.getExtension(file, extension);
    return this.availableIcons.includes(ext) ? `file-${ext}` : 'file-text';
  }


  /**
   * Abrir un archivo
   *
   */
  public async openFile(file: string, extension?: string) {

    if (!file) {
      this.toastCtrl.showWarningToast(this.translate.instant('COMMON.FILE_NOT_FOUND_MESSAGE'));
      return;
    }

    const type: fileType | null = this.getFileType(file, extension);

    switch (type) {
      case 'image':
        return this.openGallery([file]);

      case 'video':
        return this.openVideos([file]);

      default:
        return this.openDocument(file);
    }
  }


  /**
   * Abrir múltiples archivos
   *
   */
  public async openFiles(files: string[]) {

    if (files.length === 0) {
      this.toastCtrl.showWarningToast(this.translate.instant('COMMON.FILE_NOT_FOUND_MESSAGE'));
      return;
    }

    const images: string[] = [];
    const videos: string[] = [];
    const documents: string[] = [];

    for (const file of files) {
      const type: fileType | null = this.getFileType(file);

      switch (type) {
        case 'image':
          images.push(file);
          break;

        case 'video':
          videos.push(file);
          break;

        case 'document':
          documents.push(file);
          break;
      }
    }

    if (images.length > 0) {
      await this.openGallery(images);
    }

    if (videos.length > 0) {
      await this.openVideos(videos);
    }

    if (documents.length > 0) {
      await this.openDocument(documents[0]);
    }
  }


  /**
   * Abrir galería
   *
   */
  private async openGallery(images: string[]) {

    if (images.length === 0) {
      this.toastCtrl.showWarningToast(this.translate.instant('COMMON.FILE_NOT_FOUND_MESSAGE'));
      return;
    }

    const modal = await this.modalCtrl.create({
      component: GalleryPage,
      componentProps: {
        images,
      },
      cssClass: 'yu-modal'
    });

    await modal.present();
    await modal.onWillDismiss();
  }


  /**
   * Abrir videos
   *
   */
  private async openVideos(videos: string[]) {

    if (videos.length === 0) {
      this.toastCtrl.showWarningToast(this.translate.instant('COMMON.FILE_NOT_FOUND_MESSAGE'));
      return;
    }

    const modal = await this.modalCtrl.create({
      component: VideosViewerPage,
      componentProps: {
        urls: videos,
      },
      cssClass: 'force-fullscreen',
    });

    await modal.present();
    await modal.onWillDismiss();
  }


  /**
   * Abrir documento
   *
   */
  private async openDocument(file: string) {
    const isAndroid: boolean = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';

    const fileExtension: string = this.getExtension(file);
    const isGoogleViewerCompatible: boolean = this.googleViewerSupported.includes(fileExtension);

    if (isAndroid && isGoogleViewerCompatible) {
      file = `https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(file)}`;
    }

    await Browser.open({
      url: file,
      windowName: 'Documento',
      presentationStyle: 'fullscreen',
    });
  }


  /**
   * Extraer extensión de un archivo
   *
   */
  private getExtension(url: string, extension?: string): string {
    return (extension || url.split('.').pop() || '').toLowerCase().replace('.', '');
  }

}
